// PageDescription: 대면진료 독감예방접종 병원 리스트 페이지
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import React from 'react';
import 'tippy.js/dist/tippy.css';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import HospitalsInfluenzaTemplate from '@/components/templates/f2f/hospital/HospitalsInfluenzaTemplate';
import useInfluenzaHospitalListViewModel from '@/hooks/hospital/list/useInfluenzaHospitalListViewModel';
import useSearchInfluenzaHospitalsInfinityQuery from '@/hooks/hospital/search/useSearchInfluenzaHospitalsInfinityQuery';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { RegionByDepthVo } from '@/types/hospital';
import { createSearchHospitalLdJson } from '@/utils/format';

const cutCityNames = [
  '서울',
  '부산',
  '대구',
  '인천',
  '광주',
  '대전',
  '울산',
  '세종',
  '제주',
];
interface Props extends CustomPageProps {
  queries: RegionByDepthVo;
}

const HospitalsInfluenzaSeo = ({ queries }: Pick<Props, 'queries'>) => {
  const { siDoName, siGunGuName } = queries;
  const cuttedName = siDoName?.slice(0, 2) ?? '';
  const { hospitals } = useInfluenzaHospitalListViewModel();
  const hospitalJson = createSearchHospitalLdJson(hospitals);

  const isDefaultSeo = () => {
    return !siDoName;
  };

  const isSeongnamSeo = () => {
    return siGunGuName?.includes('성남시');
  };

  const isSejong = () => {
    return siDoName?.includes('세종');
  };

  const isCutSidoName = () => {
    return cutCityNames.includes(cuttedName);
  };

  const getCityTitle = () => {
    if (isSejong()) {
      return cuttedName;
    }
    if (isCutSidoName()) {
      return `${cuttedName} ${siGunGuName}`;
    }
    return `${siGunGuName}`;
  };

  const cityTitle = getCityTitle();

  if (isDefaultSeo()) {
    return (
      <NextSeo
        title="독감예방접종 병원찾기 - 닥터나우"
        description="내 근처 무료 독감주사 접종 병원 찾고, 가격 비교부터 원하는 시기 예약까지 한 번에 할 수 있어요"
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.HOSPITAL.INFLUENZA.path}`}
        openGraph={{
          title: '독감예방접종 병원찾기 - 닥터나우',
          description: '독감주사 병원 위치, 가격 확인 후 예약까지 해보세요!',
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_INFLUENZA_HOSPITAL_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
      />
    );
  }
  if (isSeongnamSeo()) {
    return (
      <>
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(hospitalJson) }}
          />
        </Head>
        <NextSeo
          title="성남시 독감예방무료접종 병원찾기(2023) - 닥터나우"
          description="성남 시민이라면 무료로 독감주사를 맞을 수 있어요. 인플루엔자(독감) 4가 백신 부담없이 무료로 원하는 시기에 예약하고 접종해 보세요!"
          canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${
            ROUTES.HOSPITAL.INFLUENZA.path
          }${siDoName ? `?siDoName=${siDoName}` : ''}${
            siGunGuName ? `&siGunGuName=${siGunGuName}` : ''
          }`}
          openGraph={{
            title: '성남시 독감예방무료접종 병원찾기(2023) - 닥터나우',
            description:
              '성남 시민이라면 무료로 예방접종 가능! 지금 예약하고 접종해 보세요!',
            images: [
              {
                url: `${process.env.NEXT_PUBLIC_INFLUENZA_HOSPITAL_IMAGE}`,
                alt: 'Og Image Alt',
              },
            ],
          }}
        />
      </>
    );
  }
  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(hospitalJson) }}
        />
      </Head>
      <NextSeo
        title={`${cityTitle} 독감예방접종 병원찾기 - 닥터나우`}
        description={`${cityTitle} 근처 무료 독감주사 접종 병원 찾고, 가격 비교부터 원하는 시기 예약까지 한 번에 할 수 있어요`}
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${
          ROUTES.HOSPITAL.INFLUENZA.path
        }${siDoName ? `?siDoName=${siDoName}` : ''}${
          siGunGuName ? `&siGunGuName=${siGunGuName}` : ''
        }`}
        openGraph={{
          title: `${cityTitle} 독감예방접종 병원찾기 - 닥터나우`,
          description: `독감주사 병원 위치, 가격 확인 후 예약까지 해보세요!`,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_INFLUENZA_HOSPITAL_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
      />
    </>
  );
};

const HospitalsInfluenzaListPage = ({ client, queries }: Props) => {
  return (
    <>
      <HospitalsInfluenzaSeo queries={queries} />
      <HospitalsInfluenzaTemplate client={client} />
    </>
  );
};

export default HospitalsInfluenzaListPage;

HospitalsInfluenzaListPage.Layout = DefaultLayout;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const queryClient = new QueryClient();
  const queries = ctx.query as RegionByDepthVo;

  await useSearchInfluenzaHospitalsInfinityQuery.prefetchQuery(
    queryClient,
    queries,
  );

  return {
    props: {
      queries,
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
    },
  };
};
